import { supabase } from '@/lib/supabase'
import { calculateSimilarity } from './translationMemory'

export interface VersionSegment {
  segment_order: number
  source_text: string
  target_text: string
  match_score: number
  match_type: 'confirmed' | 'fuzzy' | 'new'
  xml_paragraph_index?: number
}

export interface VersionAnalysis {
  segments: VersionSegment[]
  stats: {
    confirmed: number
    fuzzy: number
    new: number
    total: number
    leverage_percent: number
  }
}

export async function analyzeNewVersion(
  newSegments: Array<{ source: string; target?: string; paragraphIndex?: number }>,
  parentProjectId: string,
  userId: string,
  sourceLang: string,
  targetLang: string
): Promise<VersionAnalysis> {

  // Load user's TM for this language pair
  const { data: tmEntries } = await supabase
    .from('translation_memory')
    .select('source_text, target_text')
    .eq('created_by', userId)
    .eq('source_language', sourceLang)
    .eq('target_language', targetLang)
    .limit(5000)

  const tm = tmEntries || []

  // Load confirmed segments from parent project (highest priority)
  const { data: parentSegments } = await supabase
    .from('cat_segments')
    .select('source_text, target_text')
    .eq('project_id', parentProjectId)
    .not('target_text', 'is', null)
    .neq('target_text', '')

  const parentMap = new Map<string, string>()
  parentSegments?.forEach(s => {
    if (s.target_text) {
      parentMap.set(s.source_text.trim().toLowerCase(), s.target_text)
    }
  })

  const result: VersionSegment[] = []
  let confirmed = 0, fuzzy = 0, newCount = 0

  for (let i = 0; i < newSegments.length; i++) {
    const seg = newSegments[i]
    const sourceNorm = seg.source.trim()

    // 1. Exact match from parent project
    const parentExact = parentMap.get(sourceNorm.toLowerCase())
    if (parentExact) {
      result.push({
        segment_order: i + 1,
        source_text: sourceNorm,
        target_text: parentExact,
        match_score: 100,
        match_type: 'confirmed',
        xml_paragraph_index: seg.paragraphIndex,
      })
      confirmed++
      continue
    }

    // 2. Best TM match
    let bestScore = 0
    let bestTarget = ''

    for (const entry of tm) {
      const score = calculateSimilarity(sourceNorm, entry.source_text)
      if (score > bestScore) {
        bestScore = score
        bestTarget = entry.target_text
      }
    }

    if (bestScore === 100) {
      result.push({
        segment_order: i + 1,
        source_text: sourceNorm,
        target_text: bestTarget,
        match_score: 100,
        match_type: 'confirmed',
        xml_paragraph_index: seg.paragraphIndex,
      })
      confirmed++
    } else if (bestScore >= 75) {
      result.push({
        segment_order: i + 1,
        source_text: sourceNorm,
        target_text: bestTarget,
        match_score: Math.round(bestScore),
        match_type: 'fuzzy',
        xml_paragraph_index: seg.paragraphIndex,
      })
      fuzzy++
    } else {
      result.push({
        segment_order: i + 1,
        source_text: sourceNorm,
        target_text: '',
        match_score: Math.round(bestScore),
        match_type: 'new',
        xml_paragraph_index: seg.paragraphIndex,
      })
      newCount++
    }
  }

  const total = result.length
  return {
    segments: result,
    stats: {
      confirmed,
      fuzzy,
      new: newCount,
      total,
      leverage_percent: total > 0
        ? Math.round(((confirmed + fuzzy) / total) * 100)
        : 0,
    },
  }
}
